// src/features/text/whitespace-trimmer/WhitespaceTrimmer.tsx
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Typography, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, FormGroup, Switch, Grid
} from '@mui/material';
import TextToolWorkspace from '../components/TextToolWorkspace';
import StyledFAQ from '../../common/components/StyledFAQ';
import { trimWhitespace, TrimOptions } from '../utils/textUtils';

const WhitespaceTrimmer: React.FC = () => {
    const { t } = useTranslation('tools');
    const [inputText, setInputText] = useState<string>('');
    const [options, setOptions] = useState<TrimOptions>({
        trimLines: 'both',
        collapseWhitespace: true,
        target: 'all',
    });

    const outputText = useMemo(() => {
        if (!inputText) return '';
        return trimWhitespace(inputText, options);
    }, [inputText, options]);

    const handleOptionChange = (key: keyof TrimOptions, value: any) => {
        setOptions(prev => ({ ...prev, [key]: value }));
    };

    const faqs = [
        { q: 'text_whitespace_trimmer_faq1_q', a: 'text_whitespace_trimmer_faq1_a' },
        { q: 'text_whitespace_trimmer_faq2_q', a: 'text_whitespace_trimmer_faq2_a' },
        { q: 'text_whitespace_trimmer_faq3_q', a: 'text_whitespace_trimmer_faq3_a' },
    ];

    return (
        <Box>
            <TextToolWorkspace
                inputText={inputText}
                outputText={outputText}
                onInputChange={setInputText}
                inputLabelKey="text_common_input_label"
                outputLabelKey="text_common_output_label"
            >
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 4,
                    flexWrap: 'wrap'
                }}>
                    {/* Line Trimming Options */}
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: 0 }}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontWeight: 'medium' }}>{t('text_whitespace_trimmer_line_trim_title')}</FormLabel>
                            <RadioGroup
                                value={options.trimLines}
                                onChange={(e) => handleOptionChange('trimLines', e.target.value)}
                            >
                                <FormControlLabel value="both" control={<Radio size="small" />} label={t('text_whitespace_trimmer_line_trim_both')} />
                                <FormControlLabel value="start" control={<Radio size="small" />} label={t('text_whitespace_trimmer_line_trim_start')} />
                                <FormControlLabel value="end" control={<Radio size="small" />} label={t('text_whitespace_trimmer_line_trim_end')} />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                    {/* Character Target Options */}
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: 0 }}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontWeight: 'medium' }}>{t('text_whitespace_trimmer_target_title')}</FormLabel>
                            <RadioGroup
                                value={options.target}
                                onChange={(e) => handleOptionChange('target', e.target.value)}
                            >
                                <FormControlLabel value="all" control={<Radio size="small" />} label={t('text_whitespace_trimmer_target_all')} />
                                <FormControlLabel value="spaces" control={<Radio size="small" />} label={t('text_whitespace_trimmer_target_spaces')} />
                                <FormControlLabel value="tabs" control={<Radio size="small" />} label={t('text_whitespace_trimmer_target_tabs')} />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                    {/* Other Options */}
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: 0 }}>
                        <FormGroup>
                            <FormLabel component="legend" sx={{ fontWeight: 'medium', mb: 1 }}>{t('text_whitespace_trimmer_extra_options_title')}</FormLabel>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={options.collapseWhitespace}
                                        onChange={(e) => handleOptionChange('collapseWhitespace', e.target.checked)}
                                    />
                                }
                                label={t('text_whitespace_trimmer_collapse')}
                            />
                        </FormGroup>
                    </Box>
                </Box>
            </TextToolWorkspace>

            <Box sx={{ my: 8 }}>
                <Typography variant="h4" component="h2" gutterBottom textAlign="center">
                    {t('text_whitespace_trimmer_about_title')}
                </Typography>
                <Typography paragraph color="text.secondary" textAlign="center" maxWidth="md" mx="auto">
                    {t('text_whitespace_trimmer_about_desc')}
                </Typography>
                <StyledFAQ
                    title={t('faq_title')}
                    faqs={faqs}
                    t={t}
                />
            </Box>
        </Box>
    );
};

export default WhitespaceTrimmer;